import { Modal } from 'almanac-ui/Modal'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import { useUploads } from 'components/Design/UploadsTab/useUploads'
import { collectResults } from 'utils/query'
import { ScrollContainer } from '../ScrollContainer/ScrollContainer'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { ImagePickerProvider, useImagePickerContext } from '../ImagePickerContext'
import Styles from './Uploads.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { ImagePickerProviderProps } from '../ImagePickerContextTypes'
import { SelectionCount } from '../SelectionCount'
import { DeselectAllButton } from '../DeselectAllButton'
import { ConfirmSelectionButton } from '../ConfirmSelectionButton'
import { ImageThumbnail } from '../ImageThumbnail/ImageThumbnail'
import { AllUploadsContentProps } from './Types'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface AllUploadsProps {
  confirmLabel?: string
  onPressOutside?: () => void
}

interface Props extends AllUploadsProps, Omit<ImagePickerProviderProps, 'children'> {}

const AllUploadsContainer = ({ confirmLabel, onPressOutside, ...props }: Props) => (
  <ImagePickerProvider {...props}>
    <AllUploads confirmLabel={confirmLabel} onPressOutside={onPressOutside} />
  </ImagePickerProvider>
)

export const AllUploadsContent = ({ containerClassName }: AllUploadsContentProps) => {
  const uploadsQuery = useUploads()
  const uploads = collectResults(uploadsQuery, (data) => data.files)
  const context = useImagePickerContext()
  const { selectedImages, toggleSelectedImage, accept } = context
  const mediaFiles = getMediaFiles(uploads, accept)
  const { hasNextPage, fetchNextPage } = uploadsQuery
  const { isMobile } = useResponsiveDesign()

  return (
    <div className={cx(Styles.allUploads, containerClassName)} data-testid="upload-provider">
      <ScrollContainer>
        <div className={Styles.allUploadsGrid}>
          {mediaFiles.map((mediaFile) => (
            <div key={mediaFile.id} className={Styles.allUploadsItem}>
              <ImageThumbnail
                image={{ ...mediaFile, url: mediaFile.thumbnail_url as string }}
                onToggleSelect={toggleSelectedImage}
                isSelected={selectedImages.some((i) => i.url === mediaFile.thumbnail_url)}
                duration={mediaFile.duration}
              />
              {!isMobile && <Text variant="smallList">{mediaFile.title}</Text>}
            </div>
          ))}
        </div>
        {hasNextPage && (
          <div className={Styles.infiniteLoader}>
            <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage} />
          </div>
        )}
      </ScrollContainer>
    </div>
  )
}

const AllUploads = ({ confirmLabel, onPressOutside }: AllUploadsProps) => {
  const context = useImagePickerContext()
  const { selectedImages, onConfirm } = context

  const handleConfirm = () => {
    onConfirm(selectedImages, context)
  }

  return (
    <Modal.Root
      size="lg"
      data-testid="all-uploads"
      onPressOutside={() => {
        onPressOutside?.()
      }}
    >
      <Modal.Header
        title={
          <>
            <Modal.BackButton />
            Uploaded Media
          </>
        }
        actions={
          <div className={Styles.allUploadsActions}>
            {selectedImages.length > 0 && (
              <>
                <SelectionCount />
                <DeselectAllButton />
                <ConfirmSelectionButton onPress={handleConfirm} confirmLabel={confirmLabel} />
              </>
            )}
          </div>
        }
      />
      <AllUploadsContent />
    </Modal.Root>
  )
}

export { AllUploadsContainer as AllUploads }
